export type ModuleStructureType = 'simple' | 'breakdown'

export type VariationStatusKey = 'rejected' | 'estimated' | 'submitted' | 'recommended' | 'endorsed'

const VARIATION_STATUS_KEY_ALIASES: Record<string, VariationStatusKey> = {
  'rejected-withdrawn': 'rejected',
  rejectedwithdrawn: 'rejected',
  withdrawn: 'rejected',
  rejected: 'rejected',
  estimated: 'estimated',
  submitted: 'submitted',
  recommended: 'recommended',
  'dcwc-recommended': 'recommended',
  dcwcrecommended: 'recommended',
  endorsed: 'endorsed',
  agreed: 'endorsed',
  approved: 'endorsed',
  'pcg-superintendent-endorsed': 'endorsed',
  pcgsuperintendentendorsed: 'endorsed',
}

export const normalizeVariationStatusKey = (value?: string | null): VariationStatusKey | undefined => {
  if (!value) return undefined
  const normalized = value.trim().toLowerCase()
  if (!normalized) return undefined
  if (VARIATION_STATUS_KEY_ALIASES[normalized]) {
    return VARIATION_STATUS_KEY_ALIASES[normalized]
  }
  const compact = normalized.replace(/[^a-z0-9]+/g, '')
  return VARIATION_STATUS_KEY_ALIASES[compact]
}

export interface Address {
  street?: string
  city?: string
  state?: string
  postalCode?: string
}

export interface ProjectLeadOption {
  id: string
  displayName: string
  email: string
}

export interface Stakeholder {
  name?: string
  contactPerson?: string
  email?: string
  contactNumber?: string
  address: Address
}

export interface ProjectCoreDetails {
  projectName: string
  projectNumber: string
  programmeName?: string | null
  description?: string
  projectLeadUserId?: string
  projectLeadDisplayName?: string
  projectBudget?: number | null
  programStartDate?: string
  programCompletionDate?: string
  siteAddress: Address
}

export interface ProjectStakeholders {
  client: Stakeholder
  builder: Stakeholder
  superintendent: Stakeholder
}

export interface ProjectQuantityMetric {
  label: string
  value?: number | null
  unit?: string
}

export interface ProjectBenchmark {
  sector?: string
  secondarySector?: string
  siteType?: string
  contractType?: string
  builderTier?: string
  yearOfTender?: number | null
  monthOfTender?: number | null
  grossFloorArea?: number | null
  feca?: number | null
  uca?: number | null
  floorplates?: number | null
  levelsAboveGround?: number | null
  levelsBelowGround?: number | null
  quantityMetrics: ProjectQuantityMetric[]
}

export interface ModuleNode {
  id: string
  name: string
  children: ModuleNode[]
  color?: string
  isLocked?: boolean
  initialBudget?: number | null
  statusKey?: VariationStatusKey
  isExcluded?: boolean
}

export interface ModuleStructure {
  structureType: ModuleStructureType
  items: ModuleNode[]
  additionalProfitAndOverheadsPercent?: number | null
  creditProfitAndOverheadsPercent?: number | null
}

export interface PcgConfiguration extends ModuleStructure {
  numberOfLevels?: number | null
  contingencyType: ModuleStructureType
  groupVariationsBy?: string
  groupContingencyBy?: string
}

export interface ProjectModuleConfiguration {
  progressClaims: ModuleStructure
  contingency: ModuleStructure
  variations: ModuleStructure
  provisionalSums: ModuleStructure
  otherCosts: ModuleStructure
  pcg: PcgConfiguration
}

export interface ProjectTeamMember {
  userId: string
  displayName: string
  email?: string
  role: string
  roles?: string[]
}

export interface ProjectWizardData {
  coreDetails: ProjectCoreDetails
  stakeholders: ProjectStakeholders
  benchmark: ProjectBenchmark
  modules: ProjectModuleConfiguration
  teamMembers: ProjectTeamMember[]
}

export interface ProjectDraftSummary {
  id: number
  name: string
  projectNumber?: string
  updatedAt: string
}

export interface ProjectDraftDetail {
  id: number
  name: string
  projectNumber?: string
  projectLeadUserId?: string
  projectLeadDisplayName?: string
  currentStep: number
  created: string
  lastModified?: string
  data: ProjectWizardData
}

export interface ReferenceValue {
  id: string
  name: string
}

export interface QuantityMetricTemplate {
  label: string
  unit: string
}

export interface SectorReference extends ReferenceValue {
  secondarySectors: ReferenceValue[]
  quantityMetrics: QuantityMetricTemplate[]
}

export interface ProjectWizardReferenceData {
  sectors: SectorReference[]
  contractTypes: ReferenceValue[]
  builderTiers: ReferenceValue[]
  siteTypes: ReferenceValue[]
  yearsOfTender: ReferenceValue[]
}

export interface ProjectSummary {
  id: number
  projectNumber: string
  programmeName?: string | null
  projectName: string
  created: string
  isArchived?: boolean
}

export interface ProjectUserLookupItem {
  id: string
  displayName: string
  email: string
  roles: string[]
}

export interface StateProvinceOption {
  value: string
  label: string
  aliases?: string[]
}

export const AUSTRALIAN_STATE_PROVINCE_OPTIONS: StateProvinceOption[] = [
  { value: 'NSW', label: 'New South Wales (NSW)', aliases: ['New South Wales'] },
  { value: 'VIC', label: 'Victoria (VIC)', aliases: ['Victoria'] },
  { value: 'QLD', label: 'Queensland (QLD)', aliases: ['Queensland'] },
  { value: 'WA', label: 'Western Australia (WA)', aliases: ['Western Australia'] },
  { value: 'SA', label: 'South Australia (SA)', aliases: ['South Australia'] },
  { value: 'TAS', label: 'Tasmania (TAS)', aliases: ['Tasmania'] },
  {
    value: 'ACT',
    label: 'Australian Capital Territory (ACT)',
    aliases: ['Australian Capital Territory'],
  },
  { value: 'NT', label: 'Northern Territory (NT)', aliases: ['Northern Territory'] },
  { value: 'Other', label: 'Other' },
]

const normalizeStateProvinceLookupValue = (value?: string | null) => value?.trim().toLowerCase() ?? ''

export const resolveAustralianStateProvinceOption = (value?: string | null): StateProvinceOption | undefined => {
  const normalized = normalizeStateProvinceLookupValue(value)
  if (!normalized) {
    return undefined
  }

  return AUSTRALIAN_STATE_PROVINCE_OPTIONS.find((option) => {
    if (
      normalizeStateProvinceLookupValue(option.value) === normalized ||
      normalizeStateProvinceLookupValue(option.label) === normalized
    ) {
      return true
    }

    return option.aliases?.some((alias) => normalizeStateProvinceLookupValue(alias) === normalized) ?? false
  })
}

export const normalizeAustralianStateProvinceValue = (value?: string | null): string => {
  const trimmed = value?.trim() ?? ''
  return resolveAustralianStateProvinceOption(trimmed)?.value ?? trimmed
}

export const normalizeOptionalAustralianStateProvinceValue = (value?: string | null): string => {
  const trimmed = value?.trim() ?? ''
  if (!trimmed) {
    return ''
  }

  return resolveAustralianStateProvinceOption(trimmed)?.value ?? ''
}

export const isAustralianStateProvinceValue = (value?: string | null): boolean => {
  const trimmed = value?.trim()
  if (!trimmed) {
    return true
  }

  return Boolean(resolveAustralianStateProvinceOption(trimmed))
}

export const DEFAULT_ADDRESS: Address = {
  street: '',
  city: '',
  state: '',
  postalCode: '',
}

export const DEFAULT_STAKEHOLDER: Stakeholder = {
  name: '',
  contactPerson: '',
  email: '',
  contactNumber: '',
  address: { ...DEFAULT_ADDRESS },
}

const generateId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`

export const DEFAULT_MODULE_NODE = (overrides?: Partial<ModuleNode>): ModuleNode => ({
  id: generateId(),
  name: '',
  children: [],
  ...overrides,
})

export const DEFAULT_VARIATION_COLOR = '#A1A5B7'

export type VariationStatusConfigItem = {
  statusKey: VariationStatusKey
  name: string
  color: string
}

export const VARIATION_DEFAULT_ITEM_CONFIG: VariationStatusConfigItem[] = [
  { statusKey: 'rejected', name: 'Rejected/Withdrawn', color: '#F1416C' },
  { statusKey: 'estimated', name: 'Estimated', color: '#50CD89' },
  { statusKey: 'submitted', name: 'Submitted', color: '#009EF7' },
  { statusKey: 'recommended', name: 'DCWC Recommended', color: '#F6C000' },
  { statusKey: 'endorsed', name: 'PCG/Superintendent Endorsed', color: '#7E8299' },
]

export const createDefaultVariationItems = (): ModuleNode[] =>
  VARIATION_DEFAULT_ITEM_CONFIG.map((item) =>
    DEFAULT_MODULE_NODE({
      name: item.name,
      color: item.color,
      statusKey: item.statusKey,
      isLocked: true,
      children: [],
    })
  )

export const ensureVariationDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  const currentItems = items ?? []

  if (currentItems.length === 0) {
    return currentItems
  }

  const defaultNames = new Set(VARIATION_DEFAULT_ITEM_CONFIG.map((item) => item.name.toLowerCase()))
  const defaultKeyByName = new Map(
    VARIATION_DEFAULT_ITEM_CONFIG.map((item) => [item.name.toLowerCase(), item.statusKey])
  )
  const defaultKeys = VARIATION_DEFAULT_ITEM_CONFIG.map((item) => item.statusKey)
  const usedKeys = new Set<VariationStatusKey>()
  const normalizedItems = currentItems.map((node) => {
    const nameKey = (node.name ?? '').trim().toLowerCase()
    const statusKey =
      normalizeVariationStatusKey(node.statusKey) ??
      normalizeVariationStatusKey(nameKey) ??
      defaultKeyByName.get(nameKey)
    if (statusKey && !usedKeys.has(statusKey)) {
      usedKeys.add(statusKey)
      return { node, statusKey }
    }
    return { node, statusKey: undefined }
  })
  const remainingKeys = defaultKeys.filter((key) => !usedKeys.has(key))
  let changed = false

  const normalized = normalizedItems.map(({ node, statusKey }, index) => {
    const isDefaultName = defaultNames.has((node.name ?? '').toLowerCase())
    const shouldLock = node.isLocked || isDefaultName
    const nextColor = node.color ?? (isDefaultName ? DEFAULT_VARIATION_COLOR : undefined)
    const hadChildren = (node.children?.length ?? 0) > 0
    const resolvedStatusKey = statusKey ?? remainingKeys.shift() ?? node.statusKey

    if (
      node.isLocked !== shouldLock ||
      node.color !== nextColor ||
      hadChildren ||
      node.statusKey !== resolvedStatusKey
    ) {
      changed = true
    }

    return {
      ...node,
      isLocked: shouldLock,
      // Variations are flat; strip any nested children that might have been saved previously
      children: [],
      color: nextColor,
      statusKey: resolvedStatusKey,
    }
  })

  return changed ? normalized : currentItems
}

const CONTINGENCY_DEFAULT_ITEM_NAMES = ['Construction Contingency', 'Prolongation Contingency']

export const createDefaultContingencyItems = (): ModuleNode[] =>
  CONTINGENCY_DEFAULT_ITEM_NAMES.map((name) =>
    DEFAULT_MODULE_NODE({
      name,
      initialBudget: 0,
      children: [],
    })
  )

const ensureContingencyDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  if (!items || items.length === 0) {
    return items ?? []
  }

  let changed = false
  const normalize = (nodes: ModuleNode[]): ModuleNode[] =>
    nodes.map((node) => {
      const normalizedChildren = node.children?.length ? normalize(node.children) : node.children ?? []
      const needsBudget = node.initialBudget === undefined
      if (!needsBudget && normalizedChildren === node.children) {
        return node
      }
      changed = true
      return {
        ...node,
        initialBudget: needsBudget ? 0 : node.initialBudget,
        children: normalizedChildren,
      }
    })
  const normalized = normalize(items)
  return changed ? normalized : items
}

const PROGRESS_CLAIM_DEFAULT_ITEM_NAMES = [
  'Preliminaries',
  'Demolition',
  'Groundworks / Excavation',
  'Piling',
  'Tanking and Membranes',
  'Concrete Works',
  'Brickwork and Blockwork',
  'Structural Steel',
  'Facade Cladding',
  'Windows & Glazing',
  'Roofing',
  'Partitions and Linings',
  'Suspended Ceilings',
  'Carpet, Vinyl and Resilient Finishes',
  'Door Hardware',
  'Joinery and Fittings',
  'Metalwork',
  'Painting',
  'Mechanical Services',
  'Electrical Services',
  'Fire Services',
  'Hydraulic Services',
  'Vertical Transportation',
  'Security Services',
  'Margin',
]

export const createDefaultProgressClaimItems = (): ModuleNode[] =>
  PROGRESS_CLAIM_DEFAULT_ITEM_NAMES.map((name) =>
    DEFAULT_MODULE_NODE({
      name,
      initialBudget: 0,
      children: [],
    })
  )

const ensureProgressClaimDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  if (!items || items.length === 0) {
    return items ?? []
  }

  let changed = false
  const normalize = (nodes: ModuleNode[]): ModuleNode[] =>
    nodes.map((node) => {
      const normalizedChildren = node.children?.length ? normalize(node.children) : node.children ?? []
      const needsBudget = node.initialBudget === undefined
      if (!needsBudget && normalizedChildren === node.children) {
        return node
      }
      changed = true
      return {
        ...node,
        initialBudget: needsBudget ? 0 : node.initialBudget,
        children: normalizedChildren,
      }
    })
  const normalized = normalize(items)
  return changed ? normalized : items
}

const PROVISIONAL_SUM_DEFAULT_ITEM_NAMES = ['Provisional Sum 1']

export const createDefaultProvisionalSumItems = (): ModuleNode[] =>
  PROVISIONAL_SUM_DEFAULT_ITEM_NAMES.map((name) =>
    DEFAULT_MODULE_NODE({
      name,
      initialBudget: 0,
      children: [],
    })
  )

const ensureProvisionalSumDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  if (!items || items.length === 0) {
    return items ?? []
  }

  let changed = false
  const normalize = (nodes: ModuleNode[]): ModuleNode[] =>
    nodes.map((node) => {
      const normalizedChildren = node.children?.length ? normalize(node.children) : node.children ?? []
      const needsBudget = node.initialBudget === undefined
      if (!needsBudget && normalizedChildren === node.children) {
        return node
      }
      changed = true
      return {
        ...node,
        initialBudget: needsBudget ? 0 : node.initialBudget,
        children: normalizedChildren,
      }
    })
  const normalized = normalize(items)
  return changed ? normalized : items
}

const OTHER_COSTS_DEFAULT_ITEM_NAMES = ['Consultant Fees & Traditional Owner Engagement', 'Project Management']

export const createDefaultOtherCostItems = (): ModuleNode[] =>
  OTHER_COSTS_DEFAULT_ITEM_NAMES.map((name) =>
    DEFAULT_MODULE_NODE({
      name,
      initialBudget: 0,
      children: [],
    })
  )

const ensureOtherCostDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  if (!items || items.length === 0) {
    return items ?? []
  }

  let changed = false
  const normalize = (nodes: ModuleNode[]): ModuleNode[] =>
    nodes.map((node) => {
      const normalizedChildren = node.children?.length ? normalize(node.children) : node.children ?? []
      const needsBudget = node.initialBudget === undefined
      if (!needsBudget && normalizedChildren === node.children) {
        return node
      }
      changed = true
      return {
        ...node,
        initialBudget: needsBudget ? 0 : node.initialBudget,
        children: normalizedChildren,
      }
    })
  const normalized = normalize(items)
  return changed ? normalized : items
}

interface ModuleNodeTemplate {
  name: string
  color?: string
  isLocked?: boolean
  statusKey?: VariationStatusKey
  children?: ModuleNodeTemplate[]
}

const createNodesFromTemplate = (templates: ModuleNodeTemplate[]): ModuleNode[] =>
  templates.map((template) =>
    DEFAULT_MODULE_NODE({
      name: template.name,
      color: template.color,
      isLocked: template.isLocked,
      statusKey: template.statusKey,
      children: template.children ? createNodesFromTemplate(template.children) : [],
    })
  )

const PCG_DEFAULT_ITEM_CONFIG: ModuleNodeTemplate[] = [
  {
    name: 'MAIN WORKS',
    isLocked: true,
    children: [
      { name: 'Construction Costs', isLocked: true },
      { name: 'Provisional Sum', isLocked: true },
    ],
  },
  {
    name: 'VARIATIONS',
    isLocked: true,
    children: VARIATION_DEFAULT_ITEM_CONFIG.map((item) => ({
      name: item.name,
      color: item.color,
      statusKey: item.statusKey,
      isLocked: true,
    })),
  },
  {
    name: 'CONTINGENCY',
    isLocked: true,
    children: CONTINGENCY_DEFAULT_ITEM_NAMES.map((name) => ({ name })),
  },
  {
    name: 'OTHER COSTS',
    isLocked: true,
    children: OTHER_COSTS_DEFAULT_ITEM_NAMES.map((name) => ({ name })),
  },
]

export const createDefaultPcgItems = (): ModuleNode[] => createNodesFromTemplate(PCG_DEFAULT_ITEM_CONFIG)

const ensurePcgDefaultItems = (items?: ModuleNode[]): ModuleNode[] => {
  if (!items || items.length === 0) {
    return items ?? []
  }
  return items
}

export const ensureModuleConfigurationDefaults = (modules?: ProjectModuleConfiguration): ProjectModuleConfiguration => {
  const safeModules = modules ?? ({} as ProjectModuleConfiguration)
  const variations = safeModules.variations ?? { structureType: 'simple', items: [] }
  const contingency = safeModules.contingency ?? { structureType: 'simple', items: [] }
  const progressClaims = safeModules.progressClaims ?? { structureType: 'simple', items: [] }
  const provisionalSums = safeModules.provisionalSums ?? { structureType: 'simple', items: [] }
  const otherCosts = safeModules.otherCosts ?? { structureType: 'simple', items: [] }
  const pcg: PcgConfiguration =
    safeModules.pcg ??
    ({
      structureType: 'simple',
      contingencyType: 'breakdown',
      numberOfLevels: null,
      groupVariationsBy: 'project',
      groupContingencyBy: 'level2',
      items: [],
    } as PcgConfiguration)

  return {
    ...safeModules,
    variations: {
      ...variations,
      additionalProfitAndOverheadsPercent: variations.additionalProfitAndOverheadsPercent ?? null,
      creditProfitAndOverheadsPercent: variations.creditProfitAndOverheadsPercent ?? null,
      items: ensureVariationDefaultItems(variations.items),
    },
    contingency: {
      ...contingency,
      items: ensureContingencyDefaultItems(contingency.items),
    },
    progressClaims: {
      ...progressClaims,
      items: ensureProgressClaimDefaultItems(progressClaims.items),
    },
    provisionalSums: {
      ...provisionalSums,
      items: ensureProvisionalSumDefaultItems(provisionalSums.items),
    },
    otherCosts: {
      ...otherCosts,
      items: ensureOtherCostDefaultItems(otherCosts.items),
    },
    pcg: {
      ...pcg,
      items: ensurePcgDefaultItems(pcg.items),
    },
  }
}

export const ensureModuleStructureDefaults = (data: ProjectWizardData): ProjectWizardData => {
  return {
    ...data,
    modules: ensureModuleConfigurationDefaults(data.modules),
  }
}

export const DEFAULT_WIZARD_DATA: ProjectWizardData = {
  coreDetails: {
    projectName: '',
    projectNumber: '',
    description: '',
    projectLeadUserId: undefined,
    projectLeadDisplayName: undefined,
    projectBudget: null,
    programStartDate: undefined,
    programCompletionDate: undefined,
    siteAddress: { ...DEFAULT_ADDRESS },
  },
  stakeholders: {
    client: { ...DEFAULT_STAKEHOLDER },
    builder: { ...DEFAULT_STAKEHOLDER },
    superintendent: { ...DEFAULT_STAKEHOLDER },
  },
  benchmark: {
    sector: undefined,
    secondarySector: undefined,
    siteType: undefined,
    contractType: undefined,
    builderTier: undefined,
    yearOfTender: null,
    monthOfTender: null,
    grossFloorArea: null,
    feca: null,
    uca: null,
    floorplates: null,
    levelsAboveGround: null,
    levelsBelowGround: null,
    quantityMetrics: [],
  },
  modules: {
    progressClaims: {
      structureType: 'simple',
      items: createDefaultProgressClaimItems(),
    },
    contingency: {
      structureType: 'simple',
      items: createDefaultContingencyItems(),
    },
    provisionalSums: {
      structureType: 'simple',
      items: createDefaultProvisionalSumItems(),
    },
    otherCosts: {
      structureType: 'simple',
      items: createDefaultOtherCostItems(),
    },
    variations: {
      structureType: 'simple',
      additionalProfitAndOverheadsPercent: null,
      creditProfitAndOverheadsPercent: null,
      items: createDefaultVariationItems(),
    },
    pcg: {
      structureType: 'simple',
      contingencyType: 'breakdown',
      numberOfLevels: null,
      groupVariationsBy: 'project',
      groupContingencyBy: 'level2',
      items: createDefaultPcgItems(),
    },
  },
  teamMembers: [],
}

export const TEAM_ROLE_OPTIONS = [
  'Project Manager',
  'Director',
  'Project Director',
  'Quality Assurance',
  'External',
]
